/**
 * Structural locking logic:
 * 1. Multi-support (wide/full needing >1 distinct supports) => lock those supports.
 * 2. Cascade downward ONLY when a support feeds exactly one locked dependent.
 * 3. Never lock non-base full via cascade.
 * 4. Skip locking if all locked dependents have safe uniform fallback.
 * 5. NEW: Lock a wide if a spanning (full|wide) above mixes its locked dependents
 *    with other supports not derived from this wide (prevents uneven drop).
 */
import { getProductTypeFromMetadata } from '../index';

export function computeStructuralLocks(spanModel, metadata, baseFullId) {
  const { productSpanInfo, supportSetMap, dependentsMap } = spanModel;
  const getTypeById = (id) => getProductTypeFromMetadata(metadata?.[productSpanInfo[id].product.choiceId]?.metadata);

  const structuralLocked = new Set();

  // Initial multi-support seeding
  Object.values(productSpanInfo).forEach((info) => {
    if (info.type === 'wide' || info.type === 'full') {
      const supports = supportSetMap[info.product.stackItemId];
      if (supports.size > 1) supports.forEach((supportId) => structuralLocked.add(supportId));
    }
  });

  const queue = [...structuralLocked];

  const tryLock = (supportId) => {
    if (structuralLocked.has(supportId)) return;
    const type = getTypeById(supportId);
    if (type === 'full' && supportId !== baseFullId) return;
    structuralLocked.add(supportId);
    queue.push(supportId);
  };

  // Downward single-dependent cascade (refined: only if the support has exactly ONE dependent overall)
  while (queue.length) {
    const current = queue.pop();
    const supports = supportSetMap[current];
    if (supports && supports.size > 0) {
      supports.forEach((supportId) => {
        const deps = dependentsMap[supportId] || new Set();
        if (deps.size !== 1) return; // new guard: multiple dependents => don't early-lock
        // If its sole dependent is locked, lock this support
        const soleDepId = deps.values().next().value;
        if (structuralLocked.has(soleDepId)) {
          tryLock(supportId);
        }
      });
    }
  }

  function canLockedDependentsFallback(candidateInfo, lockedDependentsIds) {
    if (lockedDependentsIds.length === 0) return false;
    const candidateBottom = candidateInfo.bottom;
    const candidateId = candidateInfo.product.stackItemId;
    const candidateSupports = supportSetMap[candidateId] || new Set();

    // NEW: If this candidate has no supports (foundation piece), treat as having fallback (ground).
    if (candidateSupports.size === 0) return true;

    // For each locked dependent, ensure every column it occupies
    // has SOME product (other than candidate) whose top <= candidateBottom.
    return lockedDependentsIds.every((depId) => {
      const depInfo = productSpanInfo[depId];
      return depInfo.columns.every((col) => Object.values(productSpanInfo).some((other) => {
        if (other.product.stackItemId === candidateId) return false;
        if (other.top > candidateBottom) return false;
        return other.columns.includes(col);
      })
      );
    });
  }

  // NEW: detect mixed spanning product above
  function hasMixedSpanningAbove(candidateInfo, lockedDependentsIds) {
    if (!lockedDependentsIds.length) return false;
    const lockedSet = new Set(lockedDependentsIds);
    const candidateTop = candidateInfo.top;

    return Object.values(productSpanInfo).some((above) => {
      if (above.bottom < candidateTop) return false; // must start at or above candidate top
      if (above.product.stackItemId === candidateInfo.product.stackItemId) return false;
      if (!(above.type === 'full' || above.type === 'wide')) return false;

      // Supports of this above product
      const aboveSupports = supportSetMap[above.product.stackItemId];
      if (!aboveSupports || aboveSupports.size === 0) return false;

      let sharesLocked = false;
      let hasOther = false;
      aboveSupports.forEach((sid) => {
        if (lockedSet.has(sid)) sharesLocked = true;
        else hasOther = true;
      });

      return sharesLocked && hasOther;
    });
  }

  function processStructuralLocksIteration() {
    let changedLocal = false;
    Object.values(productSpanInfo).forEach((info) => {
      const id = info.product.stackItemId;
      if (structuralLocked.has(id)) return;
      const type = info.type;
      if (type !== 'wide' && type !== 'full') return;
      if (type === 'full' && id !== baseFullId) return;

      const dependents = dependentsMap[id];
      if (!dependents || dependents.size === 0) return;

      const lockedDependents = [];
      dependents.forEach((depId) => {
        if (structuralLocked.has(depId)) lockedDependents.push(depId);
      });
      if (lockedDependents.length < 2) return;

      // Column coverage
      const coveredCols = new Set();
      lockedDependents.forEach((depId) => {
        const depInfo = productSpanInfo[depId];
        depInfo.columns.forEach((col) => {
          if (info.columns.includes(col)) coveredCols.add(col);
        });
      });

      const requiredCols = type === 'wide' ? 2 : 2;
      if (coveredCols.size < requiredCols) return;

      // Mixed spanning product above (forces lock)
      if (hasMixedSpanningAbove(info, lockedDependents)) {
        structuralLocked.add(id);
        changedLocal = true;
        return;
      }

      // Fallback alignment allowed -> skip lock; else lock
      const hasFallback = canLockedDependentsFallback(info, lockedDependents);
      if (!hasFallback) {
        structuralLocked.add(id);
        changedLocal = true;
      }
    });
    return changedLocal;
  }

  let changed = true;
  while (changed) {
    changed = processStructuralLocksIteration();
  }

  function lockBetweenAdjacentFulls() {
    const fullInfos = Object.values(productSpanInfo)
      .filter((info) => info.type === 'full')
      .sort((infoA, infoB) => infoA.bottom - infoB.bottom);

    if (fullInfos.length < 2) return;

    for (let i = 0; i < fullInfos.length - 1; i += 1) {
      const lower = fullInfos[i];
      const upper = fullInfos[i + 1];

      Object.values(productSpanInfo).forEach((info) => {
        if (info.type === 'full') return;
        const id = info.product.stackItemId;
        if (structuralLocked.has(id)) return;

        // Strictly between lower.top and upper.bottom
        if (info.bottom >= lower.top && info.top <= upper.bottom) {
          structuralLocked.add(id);
        }
      });
    }
  }

  lockBetweenAdjacentFulls();

  return structuralLocked;
}
